import React from 'react';
import { motion } from 'motion/react';
import { Compass, Rocket, Gauge, ShieldCheck } from 'lucide-react';

interface HowToUseProps {
  isVisible: boolean;
}

const HowToUse: React.FC<HowToUseProps> = ({ isVisible }) => {
  if (!isVisible) return null;

  return (
    <section className="py-24 md:py-32 bg-transparent border-t border-white/[0.06]">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16 space-y-4 text-left"
        >
          <div className="flex items-center gap-3">
            <div className="w-1.5 h-6 bg-[#F26522]"></div>
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#F26522]">Operating Manual</h2>
          </div>
          <h3 className="text-3xl md:text-5xl font-medium tracking-tight text-white leading-tight">
            How to use eNvesta AI
          </h3>
        </motion.div>

        {/* Account Types Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24 grid grid-cols-1 lg:grid-cols-3 gap-8 text-left"
        >
          <div className="lg:col-span-1 space-y-4">
            <h4 className="text-xl font-medium text-white">Account Ecosystem</h4>
            <p className="text-sm text-white/60 leading-relaxed font-normal">
              You can browse anonymously or create an account. Anonymous users see limited previews. Registered users save ventures, access War Room, and unlock premium features.
            </p>
          </div>
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-3">
              <h5 className="text-xs font-bold uppercase tracking-wider text-[#F26522]">Individual Accounts</h5>
              <p className="text-xs text-white/60 leading-relaxed font-normal">
                Suit solo founders and small teams. Access all hubs with core data connections and streamlined billing.
              </p>
            </div>
            <div className="p-6 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-3">
              <h5 className="text-xs font-bold uppercase tracking-wider text-[#F26522]">Company Accounts</h5>
              <p className="text-xs text-white/60 leading-relaxed font-normal">
                Suit multi-location businesses, agencies, and institutional users. Enhanced team size, advanced data connections, and enterprise billing.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Four Hubs Section */}
        <div className="space-y-16 text-left">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex items-center gap-6"
          >
            <h4 className="text-xs uppercase tracking-[0.2em] text-white/60 font-medium">Four Hubs. One Lifecycle.</h4>
            <div className="h-[1px] flex-1 bg-white/[0.06]"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {/* Launchpad */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-4"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded bg-[#F26522] text-white flex items-center justify-center shrink-0">
                  <Rocket className="w-5 h-5" />
                </div>
                <h4 className="text-lg font-medium text-white">Launchpad</h4>
              </div>
              <p className="text-xs text-white/60 leading-relaxed font-normal">
                Validate new ideas. Enter your concept, location, sector, and budget. Get startup costs, break-even timeline, risk scores, and exact site recommendations. Save to War Room or export as professional PDF.
              </p>
            </motion.div>

            {/* Discovery Hub */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-4"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded bg-[#F26522] text-white flex items-center justify-center shrink-0">
                  <Compass className="w-5 h-5" />
                </div>
                <h4 className="text-lg font-medium text-white">Discovery Hub</h4>
              </div>
              <p className="text-xs text-white/60 leading-relaxed font-normal">
                Explore any area. Uncover what neighborhoods are actually known for. View business density heatmaps. Verify real competitors with live data. Spot gaps before you enter.
              </p>
            </motion.div>

            {/* eNvesta Optimize */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-4"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded bg-[#F26522] text-white flex items-center justify-center shrink-0">
                  <Gauge className="w-5 h-5" />
                </div>
                <h4 className="text-lg font-medium text-white">eNvesta AI Optimize</h4>
              </div>
              <p className="text-xs text-white/60 leading-relaxed font-normal">
                Scale what works. Connect your active business. Track actuals against original projections. Model expansions, pricing changes, and new locations. Get automated market alerts and living SWOT updates.
              </p>
            </motion.div>

            {/* Scrutiny Deck */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 bg-[#3a3430] border border-white/[0.06] rounded-xl space-y-4"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded bg-[#F26522] text-white flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <h4 className="text-lg font-medium text-white">Scrutiny Deck</h4>
              </div>
              <p className="text-xs text-white/60 leading-relaxed font-normal">
                Stress-test assumptions. Submit any raw idea. Receive brutal, data-backed analysis of fatal gaps, hidden assumptions, and viable pivot paths with real capital estimates.
              </p>
            </motion.div>
          </div>
        </div>

        {/* War Room Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 p-8 bg-[#3a3430] border border-white/[0.06] rounded-xl text-left space-y-3"
        >
          <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#F26522]">The War Room</h4>
          <p className="text-xl font-medium text-white tracking-tight max-w-2xl leading-snug">
            Compare saved ventures from any hub side by side. Export intelligence dossiers. Decide with clarity.
          </p>
        </motion.div>

      </div>
    </section>
  );
};

export default HowToUse;
